import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Form,
  FormField,
  Input,
  Toggle,
  Alert,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import { DivisionValidator } from 'src/validations/DivisionValidator';
import RequiredFieldLabel from '../RequiredFieldLabel';

interface DivisionFormProps {
  visible: boolean;
  onSubmit: (data: any) => void;
  onDismiss: () => void;
  apiStatus: ApiRequestStatus;
}

export interface DivisionFormData {
  name: string;
  desc: string;
  isActive: boolean;
}

const DivisionForm = ({ visible, onDismiss, apiStatus, onSubmit }: DivisionFormProps) => {
  const initValue = {
    name: '',
    desc: '',
    isActive: true,
  };
  const [formData, setFormData] = useState<DivisionFormData>(initValue);

  const { fieldValidationError, validate, setFieldValidationErrors } = useFormValidation(
    formData,
    DivisionValidator(),
  );

  // Reset form when API call is successful
  useEffect(() => {
    if (apiStatus.isSuccess) {
      handleCancelClick();
    }
  }, [apiStatus.isSuccess]);

  const resetForm = () => {
    setFieldValidationErrors({});
    setFormData(initValue);
  };

  // Cleanup when modal is closed
  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const handleCancelClick = () => {
    onDismiss();
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(formData);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancelClick}
      header="Add New Division"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="cancel-button"
              formAction="none"
              variant="link"
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSubmit}
              loading={apiStatus.isLoading}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form data-testid="division-form">
        <SpaceBetween direction="vertical" size="l">
          <FormField
            data-testid="division-name-field"
            label={<RequiredFieldLabel label="Division " />}
            errorText={fieldValidationError.name}
          >
            <Input
              data-testid="division-name-input"
              value={formData.name}
              onChange={({ detail }) =>
                setFormData((prevState) => ({
                  ...prevState,
                  name: detail.value.trim(),
                }))
              }
              placeholder="Enter division"
            />
          </FormField>

          <FormField
            data-testid="division-desc-field"
            label={<RequiredFieldLabel label="Description " />}
            errorText={fieldValidationError.desc}
          >
            <Input
              data-testid="division-desc-input"
              value={formData.desc}
              onChange={({ detail }) =>
                setFormData((prevState) => ({
                  ...prevState,
                  desc: detail.value,
                }))
              }
              placeholder="Enter Description"
            />
          </FormField>

          <FormField
            label="Activation Status"
            constraintText="Enable or disable the division using this switch."
          >
            <Toggle
              onChange={({ detail }) => {
                setFormData((prevState) => ({
                  ...prevState,
                  isActive: detail.checked,
                }));
              }}
              checked={formData.isActive}
            >
              {formData.isActive ? 'Active' : 'Inactive'}
            </Toggle>
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default DivisionForm;
